import React, { useState, Fragment, useRef, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { useHistory } from "react-router-dom"
import clsx from "clsx"
import { useTranslation } from "react-i18next"

import {
  sendOrder,
  approvePayment,
  editOrder,
  getOrder,
  pay
} from "services"
import {
  createOfflineOrder,
  validateCartContents,
  getWhatsappLinkPayMessage,
  printOrder,
  createPosPaymentRequest,
  getCashTypeForShift,
  isPosTerminalPayment,
  posTerminalRequest,
  isCloudPosPaymentRequest,
  createPcPosPaymentRequest,
  cdsBroadcastChannel
} from "utils"
import {
  addNewShiftOrder,
  editOfflineOrder,
  showWarningTopup
} from "@redux"
import {
  PcPosTerminalPaymentStatusModal,
  CartStockValidationResultModal,
  PosTerminalPaymentStatusModal
} from "components"
import CheckoutPayButtonContent from "./CheckoutPayButtonContent"
import classes from "./CheckoutPayButton.module.scss"
import isPcPosPaymentRequest from "utils/isPcPosPaymentRequest"

const CheckoutPayButton = props => {
  const {
    note,
    total,
    isLinkPay,
    onComplete,
    totalTax,
    isOnline,
    totalDiscount,
    totalCharges,
    cashTypes,
    discounts,
    deliveryFees,
    orderItems,
    orderCombos,
    printCustomerReceipt,
    deliveryServiceOutOfRange,
    snapboxQuotaionLoading,
    selectedUserAccount,
    sendPrintCustomerReceiptToPos
  } = props

  const [t] = useTranslation()

  const history = useHistory()

  const dispatch = useDispatch()


  const shiftCashier =
    useSelector(state => state.shiftCashier)

  const order =
    useSelector(state => state.order)

  const orderForEditDetails =
    useSelector(state => state.orderForEditDetails)

  const userAccountInfo =
    useSelector(state => state.userAccountInfo)

  const temporaryOff =
    useSelector(state => state.systemSettings.temporaryOff)

  const posTerminals =
    useSelector(state => state.posTerminals)

  const {
    areaName,
    deliveryType,
    deliveryAddress,
    deliveryService
  } = useSelector(state => state.deliveryInfo)

  const [sending, setSending] = useState(false)
  const [problematicItems, setProblematicItems] = useState([])
  const [posTerminalPaymentStatus, setPosTerminalPaymentStatus] = useState("")
  const [openCartValidationResultModal, setOpenCartValidationResultModal] = useState(false)
  const [openPosTerminalPaymentStatusModal, setOpenPosTerminalPaymentStatusModal] = useState(false)
  const [pcPosTerminalPaymentStatus, setPcPosTerminalPaymentStatus] = useState("")
  const [openPcPosTerminalPaymentStatusModal, setOpenPcPosTerminalPaymentStatusModal] = useState(false)

  const unmount = useRef(false)

  const sendEventsToCds = (data) => {
    const bc = cdsBroadcastChannel.get()

    if (!bc) return

    bc.postMessage({
      type: "success",
      ...data
    })
  }

  const handlePosTerminalRequestSent = () => {
    if (unmount.current) return

    setSending(false)

    setPosTerminalPaymentStatus("requestSent")
  }

  const handlePosTerminalRequestAcknowledge = () => {
    if (unmount.current) return

    setSending(false)

    setPosTerminalPaymentStatus("acknowledge")
  }

  const handlePosTerminalPaymentSuccess = () => {
    onComplete()

    history.push("/foods")
  }

  const needToEditOrder = () => {
    if (orderForEditDetails === null) return false

    const tableNumberChanged =
      order.tableNumber !==
      orderForEditDetails.tableNumber

    if (tableNumberChanged) return true

    const orderAccountChanged =
      selectedUserAccount?._id !==
      orderForEditDetails.selectedUserAccount?._id

    if (orderAccountChanged) return true

    const orderCouponCodes =
      discounts.map(discount =>
        discount.couponCode
      )

    const couponsCodesChanged =
      JSON.stringify(orderCouponCodes) !==
      JSON.stringify(orderForEditDetails.couponCodes)

    if (couponsCodesChanged) return true

    const deliveryTypeChanged =
      deliveryType !==
      orderForEditDetails.deliveryType

    if (deliveryTypeChanged) return true

    return false
  }

  const validateCart = async () => {
    const problematicItems =
      await validateCartContents(
        order.items,
        order.combos
      )

    if (problematicItems.length === 0) return true

    setProblematicItems(
      problematicItems
    )

    setOpenCartValidationResultModal(true)

    setSending(false)

    return false
  }

  const newShiftOrder = (shiftOrder) => {
    dispatch(addNewShiftOrder(shiftOrder))
  }

  const getAvailableCoupon = () => {
    const result = []

    discounts.forEach(discount =>
      result.push(discount.couponCode)
    )

    orderCombos.forEach(combo =>
      result.push(combo.code)
    )

    return result
  }

  const validate = () => {
    if (deliveryType === "dineIn" && !order.tableNumber) {
      toast.error(t("لطفا_میز_را_وارد_کنید"))

      return false
    }

    if (!deliveryAddress?.address && deliveryType === "delivery") {
      toast.error(t("لطفا_آدرس_را_وارد_کنید"))

      return false
    }

    return true
  }

  const getRequestBody = () => {
    const oItems =
      orderItems.map(item => ({
        asComboSet: false,
        optionId: item.optionId,
        foodId: item.foodId,
        quantity: item.quantity,
        systemDiscount: item.systemDiscount * item.quantity,
        specificationItemIds:
          item.specificationItems.map(si => ({
            _id: si._id,
            quantity: si.quantity,
            builderStepKey: si.builderStepKey,
            portion: si.portion
          }))
      }))

    const cItems = []
    orderCombos.forEach((combo, cIndex) =>
      combo.items.forEach(comboItem =>
        cItems.push({
          foodId: comboItem.foodId,
          optionId: comboItem.optionId,
          quantity: comboItem.quantity,
          specificationItemIds: [],
          comboSet: {
            code: combo.code,
            groupNumber: cIndex + 1,
            comboSetItemId: comboItem.comboSetItemId
          }
        })
      )
    )

    const items = [...cItems, ...oItems]

    const couponCodes = getAvailableCoupon()

    const data = {
      note,
      items,
      isCod,
      isPrinted: true,
      deliveryType,
      areaName,
      branchId: userAccountInfo.branchId,
      couponCodes,
      deliveryAddress,
      deliveryService:
        deliveryType !== "delivery" ? "internal" : deliveryService
    }

    const shiftId =
      shiftCashier[userAccountInfo._id]._id

    if (shiftId) {
      data["shiftId"] = shiftId
    }

    if (!isCod && !isLinkPay) {
      const cashType =
        getCashTypeForShift(cashTypes, total)

      data["cashType"] = cashType

      data["paymentType"] = "cash"
    }

    if (deliveryType === "delivery") {
      data["address"] = deliveryAddress

      if (deliveryService === "snapbox") {
        data["riderQuotationId"] = deliveryFees.quotationId
      }
    }

    if (deliveryType === "dineIn") {
      data["tableNumber"] = order.tableNumber
    }

    if (!isCod && isLinkPay) {
      const paymentLinkMethod =
        cashTypes[0].value === "SMS" ? "sms" : "whatsapp"

      data["paymentLinkMethod"] = paymentLinkMethod

      data["paymentType"] =
        userAccountInfo.selectedCashierBranch.defaultSmsPaymentGateway
    }

    const posTerminalName =
      cashTypes[0].terminalName

    if (sendPrintCustomerReceiptToPos && !!posTerminalName) {
      data["posTerminalName"] = posTerminalName

      data["sendPrintCustomerReceiptToPos"] = true
    }

    return data
  }

  const handleEditOrder = async () => {
    if (!validate()) return

    const posTerminalName =
      cashTypes[0].terminalName

    const posPayment =
      isPosTerminalPayment(cashTypes)

    const cloudPosPaymentRequest =
      isCloudPosPaymentRequest(cashTypes)

    const pcPosPaymentRequest =
      isPcPosPaymentRequest(cashTypes)

    try {
      if (pcPosPaymentRequest) {
        const posTerminalDetails =
          posTerminals[userAccountInfo._id]?.find(t =>
            t.terminalName === posTerminalName
          )

        if (!posTerminalDetails) return setSending(false)

        setPcPosTerminalPaymentStatus("loading")

        setOpenPcPosTerminalPaymentStatusModal(true)

        const result =
          await createPcPosPaymentRequest(
            total,
            posTerminalDetails.ipAddress,
            posTerminalDetails.comPort,
            posTerminalDetails.posPort
          )

        if (unmount.current) return

        if (result.status !== "Success") {
          setSending(false)

          setPcPosTerminalPaymentStatus("failed")

          return
        }

        setPcPosTerminalPaymentStatus("success")
      }

      if (cloudPosPaymentRequest) {
        setOpenPosTerminalPaymentStatusModal(true)

        const posPaymentResult =
          await posTerminalRequest.send(
            total,
            posTerminalName,
            handlePosTerminalRequestSent,
            handlePosTerminalRequestAcknowledge
          )

        if (unmount.current) return

        if (posPaymentResult === "canceled")
          return setSending(false)

        if (posPaymentResult === "unsuccess") {
          setSending(false)

          setPosTerminalPaymentStatus("paymentFailed")

          return
        }

        if (posPaymentResult === "requestFailed") {
          setSending(false)

          setPosTerminalPaymentStatus("failedRequest")

          return
        }
      }

      if (posPayment) {
        const posPaymentResult =
          await createPosPaymentRequest(total)

        if (!posPaymentResult) {
          dispatch(
            showWarningTopup(t("پرداخت_انجام_نشد"))
          )

          return
        }
      }

      setSending(true)

      const cashType =
        getCashTypeForShift(cashTypes, total)

      const accountId = selectedUserAccount._id

      const orderId = orderForEditDetails.orderId

      const query =
        orderForEditDetails.isGuestOrder
          ? { orderId } : { accountId }

      const body = getRequestBody()

      const response =
        await editOrder(
          orderId,
          body,
          query
        )

      await approvePayment(
        {
          orderIds: [orderId],
          cashType,
          posTerminalName,
          sendPrintCustomerReceiptToPos
        },
        query
      )

      sendEventsToCds({ message: t("ویرایش_انجام_شد") })

      const shiftOrder = {
        taxes: totalTax,
        charges: totalCharges,
        discounts: totalDiscount,
        cashTypes: cashType
      }

      newShiftOrder(shiftOrder)

      if (printCustomerReceipt) {
        const skipKitchenReceipt = true

        const skipOrderItems = true

        response.orderStatus = "paid"

        printOrder(
          response,
          skipKitchenReceipt,
          skipOrderItems
        )
      }

      if (cloudPosPaymentRequest) return

      onComplete()

      history.push("/foods")

      dispatch(
        showWarningTopup(t("پرداخت_با_موفقیت_انجام_شد"))
      )

      setSending(false)
    } catch (err) {
      console.log(err)

      dispatch(
        showWarningTopup(t("پرداخت_انجام_نشد"))
      )

      setSending(false)
    }
  }

  const handlePlaceOrder = async () => {
    if (!validate()) return

    setSending(true)

    try {
      const result = await validateCart()

      if (!result) return setSending(false)

      const posTerminalName =
        cashTypes[0].terminalName

      const posPayment =
        isPosTerminalPayment(cashTypes)

      const cloudPosPaymentRequest =
        isCloudPosPaymentRequest(cashTypes)

      const pcPosPaymentRequest =
        isPcPosPaymentRequest(cashTypes)

      if (pcPosPaymentRequest) {
        const posTerminalDetails =
          posTerminals[userAccountInfo._id]?.find(t =>
            t.terminalName === posTerminalName
          )

        if (!posTerminalDetails) return setSending(false)

        setPcPosTerminalPaymentStatus("loading")

        setOpenPcPosTerminalPaymentStatusModal(true)

        const result =
          await createPcPosPaymentRequest(
            total,
            posTerminalDetails.ipAddress,
            posTerminalDetails.comPort,
            posTerminalDetails.posPort
          )

        if (unmount.current) return

        if (result.status !== "Success") {
          setSending(false)

          setPcPosTerminalPaymentStatus("failed")

          return
        }

        setPcPosTerminalPaymentStatus("success")
      }

      if (cloudPosPaymentRequest) {
        setOpenPosTerminalPaymentStatusModal(true)

        const posPaymentResult =
          await posTerminalRequest.send(
            total,
            posTerminalName,
            handlePosTerminalRequestSent,
            handlePosTerminalRequestAcknowledge
          )

        if (unmount.current) return

        if (posPaymentResult === "canceled") {
          return setSending(false)
        }

        if (posPaymentResult === "unsuccess") {
          setSending(false)

          setPosTerminalPaymentStatus("paymentFailed")

          return
        }

        if (posPaymentResult === "requestFailed") {
          setSending(false)

          setPosTerminalPaymentStatus("failedRequest")

          return
        }
      }

      if (posPayment) {
        const posPaymentResult =
          await createPosPaymentRequest(total)

        if (!posPaymentResult) {
          dispatch(
            showWarningTopup(t("پرداخت_انجام_نشد"))
          )

          setSending(false)

          return
        }
      }

      const accountId =
        selectedUserAccount?._id || ""

      const body = getRequestBody()

      const { order } =
        await sendOrder(body, { accountId })

      sendEventsToCds({ message: t("پرداخت_انجام_شد") })

      const cashType =
        getCashTypeForShift(cashTypes, total)

      const shiftOrder = {
        taxes: totalTax,
        charges: totalCharges,
        discounts: totalDiscount,
        cashTypes: cashType
      }

      newShiftOrder(shiftOrder)

      if (printCustomerReceipt) {
        order.orderStatus = "paid"

        printOrder(order)
      }

      if (cloudPosPaymentRequest)
        return setPosTerminalPaymentStatus("paymentSuccess")

      onComplete()

      history.push("/foods")

      dispatch(
        showWarningTopup(
          t("پرداخت_با_موفقیت_انجام_شد")
        )
      )
    } catch (err) {
      console.log(err)

      dispatch(
        showWarningTopup("پرداخت_انجام_نشد")
      )

      setSending(false)
    }
  }

  const handlePressOffline = async () => {
    const posTerminalName =
      cashTypes[0].terminalName

    const posPayment =
      isPosTerminalPayment(cashTypes)

    const pcPosPaymentRequest =
      isPcPosPaymentRequest(cashTypes)

    if (pcPosPaymentRequest) {
      const posTerminalDetails =
        posTerminals[userAccountInfo._id]?.find(t =>
          t.terminalName === posTerminalName
        )

      if (!posTerminalDetails) return setSending(false)

      setPcPosTerminalPaymentStatus("loading")

      setOpenPcPosTerminalPaymentStatusModal(true)

      const result =
        await createPcPosPaymentRequest(
          total,
          posTerminalDetails.ipAddress,
          posTerminalDetails.comPort,
          posTerminalDetails.posPort
        )

      if (unmount.current) return

      if (result.status !== "Success") {
        setSending(false)

        setPcPosTerminalPaymentStatus("failed")

        return
      }

      setPcPosTerminalPaymentStatus("success")
    }

    if (posPayment) {
      const posPaymentResult =
        await createPosPaymentRequest(total)

      if (!posPaymentResult) {
        toast.error(t("تراکنش_انجام_نشد"))

        return
      }
    }

    const shiftId =
      shiftCashier[userAccountInfo._id]._id

    const cashType =
      getCashTypeForShift(cashTypes, total)

    const shiftOrder = {
      taxes: totalTax,
      charges: totalCharges,
      discounts: totalDiscount,
      cashTypes: cashType
    }

    const offlineOrderDetails = {
      note,
      total,
      order,
      branchId: userAccountInfo.branchId,
      discounts,
      shiftOrder,
      deliveryFees,
      deliveryType,
      shiftId,
      deliveryAddress,
      deliveryService,
      orderStatus: "paid",
      paidDate: new Date(),
      addedBySystem: true,
      addedByFullName: userAccountInfo.fullName
    }

    const isEditMode =
      !!orderForEditDetails?.offlineOrderId

    if (isEditMode) {
      dispatch(
        editOfflineOrder(
          orderForEditDetails?.offlineOrderId,
          offlineOrderDetails
        )
      )
    } else {
      createOfflineOrder(
        offlineOrderDetails,
        printCustomerReceipt
      )
    }

    onComplete()

    history.push("/foods")

    dispatch(
      showWarningTopup(
        t("پرداخت_با_موفقیت_انجام_شد")
      )
    )
  }

  const validateLinkPayCheckout = () => {
    if (!selectedUserAccount) {
      toast.error(t("ابتدا_کاربر_خود_را_انتخاب_کنید"))

      return false
    }

    if (deliveryType === "dineIn" && !order.tableNumber) {
      toast.error(t("لطفا_میز_را_وارد_کنید"))

      return false
    }

    if (deliveryType === "delivery" && !deliveryAddress?.address) {
      toast.error(t("لطفا_آدرس_را_وارد_کنید"))

      return false
    }

    return true
  }

  const sendWhatsappLinkPayMessage = (paymentId, order) => {
    const message =
      getWhatsappLinkPayMessage(order, paymentId)

    const url =
      `https://api.whatsapp.com/send?phone=${order.customerMobilePhone}&text=${encodeURIComponent(message)}`

    window.open(url, "_blank")
  }

  const handleCodLinkPayCheckout = async () => {
    if (!validateLinkPayCheckout()) return

    try {
      const result = await validateCart()

      if (!result) return

      const paymentLinkMethod =
        cashTypes[0].value === "SMS" ? "sms" : "whatsapp"

      const query =
        orderForEditDetails.isGuestOrder
          ? { orderId: orderForEditDetails.orderId }
          : { accountId: orderForEditDetails.accountId }

      const needEdit = needToEditOrder()

      let editedOrder

      if (needEdit) {
        const body = getRequestBody()

        editedOrder =
          await editOrder(
            orderForEditDetails.orderId,
            body,
            query
          )
      }

      const payment =
        await pay(
          {
            orderIds: [orderForEditDetails.orderId],
            type: "zarinpal",
            paymentLinkMethod
          },
          query
        )

      sendEventsToCds({ message: t("لینک_پرداخت_ارسال_شد") })

      const isWhatsappLinkPay =
        paymentLinkMethod === "واتساپ"

      if (isWhatsappLinkPay) {
        const order =
          editedOrder ||
          await getOrder(orderForEditDetails.orderId, true)

        sendWhatsappLinkPayMessage(
          payment._id,
          order
        )
      }

      onComplete()

      history.push("/foods")

      dispatch(
        showWarningTopup(t("لینک_با_موفقیت_ارسال_شد"))
      )
    } catch (err) {
      console.log(err)

      toast.error(t("لینک_ارسال_نشد"))

      setSending(false)
    }
  }

  const handleLinkPayCheckout = async () => {
    if (!validateLinkPayCheckout()) return

    setSending(true)

    try {
      const result = await validateCart()

      if (!result) return

      const accountId =
        selectedUserAccount._id

      const body = getRequestBody()

      const { order, payment } =
        await sendOrder(body, { accountId })

      sendEventsToCds({ message: t("لینک_پرداخت_ارسال_شد") })

      const paymentLinkMethod =
        cashTypes[0].value

      const isWhatsappLinkPay =
        paymentLinkMethod === "واتساپ"

      isWhatsappLinkPay &&
        sendWhatsappLinkPayMessage(
          payment._id,
          order
        )

      onComplete()

      history.push("/foods")

      dispatch(
        showWarningTopup(t("لینک_با_موفقیت_ارسال_شد"))
      )
    } catch (err) {
      console.log(err)

      toast.error(t("لینک_ارسال_نشد"))

      setSending(false)
    }
  }

  const handleCheckoutCodOrder = async () => {
    const posTerminalName =
      cashTypes[0].terminalName

    const posPayment =
      isPosTerminalPayment(cashTypes)

    const cloudPosPaymentRequest =
      isCloudPosPaymentRequest(cashTypes)

    const pcPosPaymentRequest =
      isPcPosPaymentRequest(cashTypes)

    try {
      if (pcPosPaymentRequest) {
        const posTerminalDetails =
          posTerminals[userAccountInfo._id]?.find(t =>
            t.terminalName === posTerminalName
          )

        if (!posTerminalDetails) return setSending(false)

        setPcPosTerminalPaymentStatus("loading")

        setOpenPcPosTerminalPaymentStatusModal(true)

        const result =
          await createPcPosPaymentRequest(
            total,
            posTerminalDetails.ipAddress,
            posTerminalDetails.comPort,
            posTerminalDetails.posPort
          )

        if (unmount.current) return

        if (result.status !== "Success") {
          setSending(false)

          setPcPosTerminalPaymentStatus("failed")

          return
        }

        setPcPosTerminalPaymentStatus("success")
      }

      if (cloudPosPaymentRequest) {
        setOpenPosTerminalPaymentStatusModal(true)

        const posPaymentResult =
          await posTerminalRequest.send(
            total,
            posTerminalName,
            handlePosTerminalRequestSent,
            handlePosTerminalRequestAcknowledge
          )

        if (unmount.current) return

        if (posPaymentResult === "canceled") return

        if (posPaymentResult === "unsuccess") {
          setPosTerminalPaymentStatus("paymentFailed")

          return
        }

        if (posPaymentResult === "requestFailed") {
          setSending(false)

          setPosTerminalPaymentStatus("failedRequest")

          return
        }
      }

      if (posPayment) {
        const posPaymentResult =
          await createPosPaymentRequest(total)

        if (!posPaymentResult) {
          dispatch(
            showWarningTopup(t("پرداخت_انجام_نشد"))
          )

          return
        }
      }

      setSending(true)

      const cashType =
        getCashTypeForShift(cashTypes, total)

      const approvePaymentQuery =
        !!orderForEditDetails.isGuestOrder
          ? { orderId: orderForEditDetails.orderId }
          : { accountId: selectedUserAccount?._id }

      const shiftId =
        shiftCashier[userAccountInfo._id]._id

      await approvePayment(
        {
          orderIds: [orderForEditDetails.orderId],
          cashType,
          shiftId,
          posTerminalName,
          sendPrintCustomerReceiptToPos
        },
        approvePaymentQuery
      )

      sendEventsToCds({ message: t("پرداخت_انجام_شد") })

      const shiftOrder = {
        taxes: totalTax,
        charges: totalCharges,
        discounts: totalDiscount,
        cashTypes: cashType
      }

      newShiftOrder(shiftOrder)

      const paidOrder =
        await getOrder(orderForEditDetails.orderId, true)

      if (cloudPosPaymentRequest) return

      onComplete()

      history.push("/foods")

      printCustomerReceipt &&
        printOrder(paidOrder)

      dispatch(
        showWarningTopup(t("پرداخت_با_موفقیت_انجام_شد"))
      )
    } catch (err) {
      dispatch(
        showWarningTopup(t("پرداخت_انجام_نشد"))
      )

      setSending(false)

      setOpenPcPosTerminalPaymentStatusModal(false)
    }
  }

  const handlePress = () => {
    const isCodLinkPay = isCod && isLinkPay

    if (isCodLinkPay) return handleCodLinkPayCheckout()

    if (isLinkPay) return handleLinkPayCheckout()

    if (!isCod) return handlePlaceOrder()

    const needEdit = needToEditOrder()

    if (needEdit) return handleEditOrder()

    handleCheckoutCodOrder()
  }

  useEffect(() => {
    unmount.current = false

    return () => unmount.current = true
  }, [])

  const sumOfPayments =
    cashTypes.reduce((t, cashType) =>
      t += Number(cashType.amount)
      , 0
    )

  const change = total - sumOfPayments

  const isCod = !!orderForEditDetails

  const internalDeliveryDisabled =
    deliveryService === "internal" &&
    deliveryAddress.areaName === "Other Area"

  const disabledButton =
    sending ||
    change > 0 ||
    temporaryOff ||
    snapboxQuotaionLoading ||
    deliveryServiceOutOfRange ||
    internalDeliveryDisabled ||
    cashTypes.length === 0

  return (
    <Fragment>
      <div className={classes["container"]}>
        <button
          disabled={disabledButton}
          onClick={isOnline ? handlePress : handlePressOffline}
          className={clsx(
            classes["button"],
            disabledButton && classes["disabled"]
          )}
        >
          <CheckoutPayButtonContent
            sending={sending}
            cashTypes={cashTypes}
            isLinkPay={isLinkPay}
            deliveryType={deliveryType}
            internalDeliveryDisabled={internalDeliveryDisabled}
            deliveryServiceOutOfRange={deliveryServiceOutOfRange}
            snapboxQuotaionLoading={snapboxQuotaionLoading}
          />
        </button>
      </div>

      <CartStockValidationResultModal
        problematicItems={problematicItems}
        openCartValidationResultModal={openCartValidationResultModal}
        setOpenCartValidationResultModal={setOpenCartValidationResultModal}
      />

      <PosTerminalPaymentStatusModal
        onPaymentSuccess={handlePosTerminalPaymentSuccess}
        posTerminalPaymentStatus={posTerminalPaymentStatus}
        setPosTerminalPaymentStatus={setPosTerminalPaymentStatus}
        openPosTerminalPaymentStatusModal={openPosTerminalPaymentStatusModal}
        setOpenPosTerminalPaymentStatusModal={setOpenPosTerminalPaymentStatusModal}
      />

      <PcPosTerminalPaymentStatusModal
        pcPosTerminalPaymentStatus={pcPosTerminalPaymentStatus}
        openPcPosTerminalPaymentStatusModal={openPcPosTerminalPaymentStatusModal}
        setOpenPcPosTerminalPaymentStatusModal={setOpenPcPosTerminalPaymentStatusModal}
      />
    </Fragment>
  )
}

export default CheckoutPayButton
